import type {
  SpiffReportColumn,
  SpiffReportFilter,
  SpiffReportMetadata,
} from "@/api/dto/process-instance";
import type { CaseStatus } from "@/domain/case/case";
import type { CaseConfig } from "@/domain/case/case-config";
import { toSpiffStatuses } from "@/domain/case/to-spiff-statuses";

/**
 * 一覧のフィルタ条件（URL と同期する状態）。
 * 日付は "YYYY-MM-DD"。
 */
export type CaseListFilters = {
  /**
   * 対象範囲。false（既定）＝自分が起票した案件のみ、true ＝関与した案件も含む
   * （承認・差し戻しをしたもの）。`for-me` は元々後者を返すので、true のときは
   * 起票者の絞り込みを外すだけ。
   */
  readonly involved?: boolean;
  readonly keyword: string;
  readonly statuses: readonly CaseStatus[];
  readonly processModelId: string | null;
  readonly submittedFrom: string | null;
  readonly submittedTo: string | null;
};

/** 一覧の対象範囲。`mine` は自分が起票した案件のみ（既定）、`all` は全案件モニタ用。 */
export type CaseListScope = "mine" | "all";

/**
 * 一覧 API（POST /process-instances/for-me または /process-instances）の body に渡す
 * report_metadata を組み立てる。
 *
 * system report（GET /process-instances/report-metadata）を使わない理由:
 *  - 一覧は完了・差し戻しも含める必要があるが、system report は process_status を
 *    非終了に固定しており結局上書きが必要
 *  - metadata 列（件名・判定）を自力で足す必要がある
 *  - リクエストが 1 本で済む
 *  - 純関数としてテストできる
 */
export function buildCaseReportMetadata(
  filters: CaseListFilters,
  config: CaseConfig,
  options: {
    readonly scope?: CaseListScope;
    /**
     * 起票者の username（`useAuth().user`）。`mine` のときだけ使う。
     * 渡さなければ絞り込まない（識別子が取れない状況で一覧を空にしない）。
     */
    readonly initiatorUsername?: string | null;
  } = {},
): SpiffReportMetadata {
  return {
    columns: buildColumns(config),
    filter_by: buildFilters(
      filters,
      config,
      options.scope ?? "mine",
      options.initiatorUsername ?? null,
    ),
    order_by: ["-start_in_seconds", "-id"],
    // 保存ビューから復元するために UI の指定をそのまま同梱する。
    // backend は未知のキーを保持して返すので、生ステータスから逆算しなくて済む。
    ui_filters: {
      involved: filters.involved ?? false,
      keyword: filters.keyword,
      statuses: filters.statuses,
      processModelId: filters.processModelId,
      submittedFrom: filters.submittedFrom,
      submittedTo: filters.submittedTo,
    },
  };
}

function buildColumns(config: CaseConfig): readonly SpiffReportColumn[] {
  const builtin: readonly SpiffReportColumn[] = [
    { Header: "Id", accessor: "id", filterable: false },
    { Header: "Status", accessor: "status", filterable: false },
    {
      Header: "Process",
      accessor: "process_model_display_name",
      filterable: false,
    },
    { Header: "Start", accessor: "start_in_seconds", filterable: false },
    { Header: "End", accessor: "end_in_seconds", filterable: false },
    {
      Header: "Last milestone",
      accessor: "last_milestone_bpmn_name",
      filterable: false,
    },
  ];

  // builtin 以外の accessor は backend が process instance metadata として
  // OUTER JOIN する（未設定なら null）。columns を空にすると builtin に
  // 差し替えられてしまうため、必要な列は必ず明示する。
  const metadata = config.metadataKeys.map((key) => ({
    Header: key,
    accessor: key,
    filterable: false,
  }));

  return [...builtin, ...metadata];
}

function buildFilters(
  filters: CaseListFilters,
  config: CaseConfig,
  scope: CaseListScope,
  initiatorUsername: string | null,
): readonly SpiffReportFilter[] {
  const result: SpiffReportFilter[] = [
    // 「現在ステップ」と下書き判定に必要
    {
      field_name: "with_oldest_open_task",
      field_value: true,
      operator: "equals",
    },
  ];

  // 全案件モニタ（管理）は自分の案件に絞らない。
  //
  // `for-me` は「関与している案件」なので、承認しただけの他人の案件も返る
  // （実測: 承認者の for-me に申請者の案件が並ぶ）。マイ案件・要対応・通知は
  // 起票者で絞る。
  //
  // `initiated_by_me` は**保存済みレポート専用**で、ここで組むアドホックな
  // report_metadata では backend が展開しない（`compile_report` を通らない）。
  // 実際に効くのは `process_initiator_username`。
  if (
    scope === "mine" &&
    initiatorUsername !== null &&
    filters.involved !== true
  ) {
    result.unshift({
      field_name: "process_initiator_username",
      field_value: initiatorUsername,
      operator: "equals",
    });
  }

  const statuses = toSpiffStatuses(filters.statuses);
  if (statuses.length > 0) {
    // backend が field_value.split(",") するのでカンマ区切り文字列で渡す
    result.push({
      field_name: "process_status",
      field_value: statuses.join(","),
      operator: "equals",
    });
  }

  if (filters.processModelId !== null) {
    result.push({
      field_name: "process_model_identifier",
      field_value: filters.processModelId,
      operator: "equals",
    });
  }

  const from = startOfDayEpoch(filters.submittedFrom);
  if (from !== null) {
    result.push({
      field_name: "start_from",
      field_value: from,
      operator: "equals",
    });
  }

  const to = endOfDayEpoch(filters.submittedTo);
  if (to !== null) {
    result.push({
      field_name: "start_to",
      field_value: to,
      operator: "equals",
    });
  }

  const keyword = filters.keyword.trim();
  if (keyword !== "") {
    // 件名 metadata への contains（backend で LIKE %value%）。全件が対象になる
    // 代わりに backend で INNER JOIN されるため、件名 metadata を持たない案件は
    // 結果に出ない（metadata は起票時に metadata_extraction_paths に従って
    // 抽出されるので、設定前に起票された案件は値を持たない）。
    result.push({
      field_name: config.titleMetadataKey,
      field_value: keyword,
      operator: "contains",
    });
  }

  return result;
}

/** "YYYY-MM-DD" → その日の 00:00:00 UTC の epoch 秒。不正な値は null。 */
function startOfDayEpoch(date: string | null): number | null {
  const parsed = parseDate(date);
  return parsed === null ? null : Math.floor(parsed / 1000);
}

/** "YYYY-MM-DD" → その日いっぱいを含む epoch 秒（翌日 00:00:00 の 1 秒前）。 */
function endOfDayEpoch(date: string | null): number | null {
  const parsed = parseDate(date);
  if (parsed === null) return null;

  const nextDay = parsed + 24 * 60 * 60 * 1000;
  return Math.floor(nextDay / 1000) - 1;
}

function parseDate(date: string | null): number | null {
  if (date === null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

  const parsed = Date.parse(`${date}T00:00:00Z`);
  return Number.isNaN(parsed) ? null : parsed;
}
